import { formatISO } from "date-fns";
import { type FC, Fragment } from "react";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { ErrorMessageBanner } from "../ErrorMessageBanner";
import { LabeledCheckbox } from "../LabeledCheckbox";

import type { GradeFormValues } from "./types";

import { AssessmentCategory } from "api/api-types-assessment";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { CircleCheckFilledIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectDesignatedSection, selectSectionScorePageCount } from "store/selectors";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { isLinkedSection } from "utils/publishing";

interface GradeFormProps {
  className?: string;
  form: UseFormReturn<GradeFormValues>;
}

export const GradeForm: FC<GradeFormProps> = ({ className, form }) => {
  const currentSection = useAppSelector(selectDesignatedSection);
  const sectionId = currentSection?.id ?? null;
  const pageCount = useAppSelector((s) => selectSectionScorePageCount(s, sectionId));
  const { isArchived } = useIsArchived();

  const enabled = form.watch("section_score_enabled");

  if (!currentSection) {
    return null;
  }

  const archived = isArchived(currentSection);
  const disabled = isLinkedSection(currentSection) || archived;

  const toggleSectionScoresPublished = () =>
    storeApi.sections.partial_update({
      id: currentSection.id,
      section_score_published_at: currentSection.section_score_published_at
        ? null
        : formatISO(new Date()),
    });

  const ASSESSMENT_CATEGORIES = [
    AssessmentCategory.prepwork,
    AssessmentCategory.activities,
    AssessmentCategory.assignments,
  ];

  return (
    <FormProvider {...form}>
      <div
        className={twMerge(
          "flex flex-col text-sm font-medium leading-[1.3125rem] text-black-tint-10",
          className,
        )}
      >
        <div className="flex flex-col gap-6">
          <LabeledCheckbox
            eventName="Section Settings - Course score toggle"
            eventProps={{ sectionTitle: currentSection.title }}
            registration={form.register("section_score_enabled", { disabled })}
          >
            {t("settings.section.section_score_enabled")}
          </LabeledCheckbox>

          <fieldset className="flex flex-col gap-2">
            <p>{t("settings.section_score_description")}</p>
            {ASSESSMENT_CATEGORIES.map((category) => (
              <Fragment key={category}>
                <label className="flex flex-row items-center justify-between">
                  <div>{t(`settings.section.section_score_${category}_percentage`)}</div>
                  <div
                    className="mds-text-field-icon data-[disabled]:cursor-not-allowed data-[disabled]:bg-black-tint-90 data-[disabled]:text-black-tint-55"
                    data-disabled={!enabled || disabled || undefined}
                  >
                    <input
                      className="mds-text-field mds-text-field-number text-black-tint-20 disabled:cursor-not-allowed"
                      {...form.register(`section_score_${category}_percentage`, {
                        disabled: !enabled || disabled,
                        setValueAs: (value: string): number => {
                          if (value === "") {
                            return 0;
                          }
                          return +value;
                        },
                        validate: (value: number) => {
                          if (!Number.isInteger(value) || value < 0) {
                            return t("error.whole_number_required");
                          }
                        },
                      })}
                    />
                    <span>%</span>
                  </div>
                </label>
                <ErrorMessageBanner name={`section_score_${category}_percentage`} />
              </Fragment>
            ))}
            <ErrorMessageBanner name="root.section_score_percentages" />
          </fieldset>

          <div className="flex flex-col gap-2 text-black-tint-20">
            {currentSection.section_score_published_at ? (
              <IconText
                iconStart={<CircleCheckFilledIcon className="text-green" />}
                text={t("settings.section_scores_published")}
              />
            ) : (
              <div>{t("settings.section_scores_unpublished")}</div>
            )}

            <div className="text-sm font-normal">
              {currentSection.section_score_published_at
                ? t("settings.section_scores_description_published")
                : t("settings.section_scores_description_unpublished")}
            </div>

            <div className="text-sm font-normal">
              {t("settings.section_scores_assessment_count", { count: pageCount })}
            </div>

            <SquareButton
              disabled={!currentSection.section_score_enabled}
              eventName={
                currentSection.section_score_published_at
                  ? "Section Settings - Unpublish Course Scores"
                  : "Section Settings - Publish Course Scores"
              }
              kind={currentSection.section_score_published_at ? "destructive" : "success"}
              size="xs"
              onClick={toggleSectionScoresPublished}
            >
              {currentSection.section_score_published_at
                ? t("settings.unpublish_section_scores")
                : t("settings.publish_section_scores")}
            </SquareButton>
          </div>
        </div>
      </div>
    </FormProvider>
  );
};
